import type { Workspace } from './model'
import { addDays, daysBetween, localDate, validDate } from './time'

export interface RosterSourceRow {
  key: string
  personId: string
  externalId?: string
  businessDate: string
  start: string
  end: string
  kind: 'work' | 'unavailable'
}
export interface RosterSourceSnapshot {
  id: string
  connector: 'quinyx'
  name: string
  hash: string
  sourceRevision?: string
  sourceUpdatedAt?: string
  acceptedAt?: string
  timeZone: string
  from: string
  to: string
  dates: string[]
  complete: boolean
  mode: 'snapshot' | 'delta'
  rows: RosterSourceRow[]
}
export interface RosterExternalAction {
  id: string
  correctionId: string
  fingerprint: string
  personId: string
  businessDate: string
  snapshotId: string
  recordedAt: string
  actor: string
  note?: string
  desiredSignature: string
}
export interface RosterCorrection {
  id: string
  fingerprint: string
  personId: string
  businessDate: string
  snapshot: RosterSourceSnapshot
  source: RosterSourceRow[]
  current: RosterSourceRow[]
  kind: 'added' | 'removed' | 'hours' | 'absence'
  status: 'pending' | 'awaiting-confirmation'
  action?: RosterExternalAction
}
const hash = (value: string) => {
  let result = 2166136261
  for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619)
  return (result >>> 0).toString(36)
}
const keyOf = (personId: string, date: string) => JSON.stringify([personId, date])
/** Equivalent splitting does not manufacture an external correction. Original rows remain in evidence. */
export function rosterSignature(rows: Pick<RosterSourceRow, 'start' | 'end' | 'kind'>[]): string {
  const result: { start: string; end: string; kind: string }[] = []
  for (const kind of ['work', 'unavailable']) {
    const sorted = rows.filter(row => row.kind === kind).map(row => ({ start: new Date(row.start).toISOString(), end: new Date(row.end).toISOString(), kind })).sort((a, b) => a.start.localeCompare(b.start))
    for (const row of sorted) {
      const previous = result[result.length - 1]
      if (previous?.kind === kind && row.start <= previous.end) previous.end = previous.end > row.end ? previous.end : row.end
      else result.push(row)
    }
  }
  return JSON.stringify(result)
}
function sourceDays(state: Workspace) {
  const result = new Map<string, { personId: string; date: string; rows: RosterSourceRow[]; snapshot: RosterSourceSnapshot; complete: boolean }>()
  for (const snapshot of state.rosterSnapshots ?? []) {
    const represented = new Map<string, RosterSourceRow[]>()
    for (const row of snapshot.rows) {
      const key = keyOf(row.personId, row.businessDate)
      represented.set(key, [...(represented.get(key) ?? []), row])
    }
    if (snapshot.complete && snapshot.mode === 'snapshot') {
      const people = new Set([...state.people.map(person => person.id), ...[...result.values()].map(day => day.personId)])
      for (const date of snapshot.dates) for (const personId of people) {
        result.set(keyOf(personId, date), { personId, date, rows: [], snapshot, complete: true })
      }
    }
    for (const [key, rows] of represented) {
      const previous = result.get(key)
      // A merge cannot prove that an omitted source interval disappeared.
      const combined = snapshot.complete ? rows : [...(previous?.rows ?? []), ...rows].filter((row, index, all) => all.findIndex(other => other.start === row.start && other.end === row.end && other.kind === row.kind) === index)
      result.set(key, { personId: rows[0].personId, date: rows[0].businessDate, rows: combined, snapshot, complete: snapshot.complete || Boolean(previous?.complete) })
    }
  }
  return result
}
export function knownRosterDay(state: Workspace, personId: string, date: string) { return sourceDays(state).get(keyOf(personId, date)) }
export function removedSourceWindows(state: Workspace, personId: string, date: string): { start: string; end: string }[] {
  const source = knownRosterDay(state, personId, date)?.rows.filter(row => row.kind === 'work') ?? []
  const actual = state.availability.filter(row => row.personId === personId && row.businessDate === date)
  const current = [...actual, ...actual.filter(row => row.source === 'quinyx' && row.localOverride && row.baseStart && row.baseEnd).map(row => ({ ...row, start: row.baseStart!, end: row.baseEnd! }))]
  return source.flatMap(row => {
    let pieces = [{ start: row.start, end: row.end }]
    for (const interval of current) pieces = pieces.flatMap(piece => interval.start >= piece.end || interval.end <= piece.start ? [piece] : [...(piece.start < interval.start ? [{ start: piece.start, end: interval.start }] : []), ...(piece.end > interval.end ? [{ start: interval.end, end: piece.end }] : [])])
    return pieces
  })
}
export function rosterCorrections(state: Workspace): RosterCorrection[] {
  const result: RosterCorrection[] = []
  for (const day of sourceDays(state).values()) {
    const current: RosterSourceRow[] = state.availability.filter(row => row.personId === day.personId && row.businessDate === day.date).map(row => ({ key: row.id, personId: row.personId, businessDate: row.businessDate, start: row.start, end: row.end, kind: row.kind ?? 'work' }))
    const sourceSignature = rosterSignature(day.rows), currentSignature = rosterSignature(current)
    const coveredCurrent = day.complete ? current : current.flatMap(row => day.rows.filter(source => source.start < row.end && source.end > row.start).map(source => ({ ...row, start: source.start > row.start ? source.start : row.start, end: source.end < row.end ? source.end : row.end })))
    if (sourceSignature === rosterSignature(coveredCurrent)) continue
    const id = `roster-${hash(keyOf(day.personId, day.date))}`
    const fingerprint = hash(JSON.stringify([id, day.snapshot.id, sourceSignature, currentSignature]))
    const action = [...(state.rosterActions ?? [])].reverse().find(item => item.correctionId === id && (item.fingerprint === fingerprint || !day.snapshot.complete && item.desiredSignature === currentSignature))
    const absence = (state.absences ?? []).some(item => !item.cancelledAt && item.personId === day.personId && day.rows.some(row => item.start < row.end && item.end > row.start))
    result.push({ id, fingerprint, personId: day.personId, businessDate: day.date, snapshot: day.snapshot, source: day.rows, current, kind: current.some(row => row.kind === 'unavailable') || absence ? 'absence' : !day.rows.length ? 'added' : !current.length ? 'removed' : 'hours', status: action ? 'awaiting-confirmation' : 'pending', action })
  }
  return result.sort((a, b) => a.businessDate.localeCompare(b.businessDate) || a.personId.localeCompare(b.personId))
}
export function rosterActionHistory(state: Workspace) {
  const days = sourceDays(state)
  return [...(state.rosterActions ?? [])].reverse().map(action => {
    const source = days.get(keyOf(action.personId, action.businessDate))
    const newer = source && source.snapshot.id !== action.snapshotId && source.snapshot.complete
    return { ...action, status: newer ? rosterSignature(source.rows) === action.desiredSignature ? 'confirmed' as const : 'not-confirmed' as const : 'awaiting-confirmation' as const, confirmingSnapshot: newer ? source.snapshot : undefined }
  })
}
export function applyRosterSnapshot(state: Workspace, snapshot: RosterSourceSnapshot, at: string): Workspace {
  if (snapshot.connector !== 'quinyx' || !snapshot.id || !snapshot.hash || snapshot.complete && snapshot.mode !== 'snapshot' || !validDate(snapshot.from) || !validDate(snapshot.to) || snapshot.to < snapshot.from || !snapshot.dates.length || snapshot.dates.some(date => !validDate(date) || date < snapshot.from || date > snapshot.to)) throw new Error('The source roster has invalid coverage.')
  if (daysBetween(snapshot.from, snapshot.to) > 366 || snapshot.mode === 'snapshot' && snapshot.complete && Array.from({ length: daysBetween(snapshot.from, snapshot.to) + 1 }, (_, index) => addDays(snapshot.from, index)).some(date => !snapshot.dates.includes(date))) throw new Error('Complete source coverage must include every declared date.')
  try { new Intl.DateTimeFormat('en', { timeZone: snapshot.timeZone }) } catch { throw new Error('The source roster timezone is invalid.') }
  for (const row of snapshot.rows) if (!state.people.some(person => person.id === row.personId) || !snapshot.dates.includes(row.businessDate) || !Number.isFinite(Date.parse(row.start)) || !Number.isFinite(Date.parse(row.end)) || Date.parse(row.end) <= Date.parse(row.start) || localDate(row.start, snapshot.timeZone) !== row.businessDate || !['work', 'unavailable'].includes(row.kind)) throw new Error('The source roster contains an invalid employee interval.')
  const existing = state.rosterSnapshots ?? []
  if (existing.some(item => item.id === snapshot.id && item.hash !== snapshot.hash)) throw new Error('The source snapshot identity was reused for another file.')
  if (existing.some(item => item.id === snapshot.id || item.hash === snapshot.hash && item.from === snapshot.from && item.to === snapshot.to && item.mode === snapshot.mode)) return state
  const version = (value?: string) => value && /^\d+$/.test(value) ? Number(value) : value && /^\d{4}-\d{2}-\d{2}T/.test(value) && Number.isFinite(Date.parse(value)) ? Date.parse(value) : undefined
  const incoming = version(snapshot.sourceUpdatedAt ?? snapshot.sourceRevision)
  if (incoming !== undefined && existing.some(item => item.from <= snapshot.to && item.to >= snapshot.from && version(item.sourceUpdatedAt ?? item.sourceRevision) !== undefined && version(item.sourceUpdatedAt ?? item.sourceRevision)! > incoming)) throw new Error('This source roster is older than the accepted source coverage.')
  return { ...state, rosterSnapshots: [...existing, { ...structuredClone(snapshot), rows: snapshot.rows.map(row => ({ ...row, start: new Date(row.start).toISOString(), end: new Date(row.end).toISOString() })), acceptedAt: at }] }
}
export function markRosterUpdated(state: Workspace, payload: { correctionId: string; fingerprint: string; note?: string }, at: string, actor: string): Workspace {
  const correction = rosterCorrections(state).find(item => item.id === payload.correctionId && item.fingerprint === payload.fingerprint)
  if (!correction) throw new Error('The roster difference changed. Review the current comparison before recording an external update.')
  if (correction.action) return state
  const action: RosterExternalAction = { id: `roster-action-${hash(`${correction.fingerprint}:${at}`)}`, correctionId: correction.id, fingerprint: correction.fingerprint, personId: correction.personId, businessDate: correction.businessDate, snapshotId: correction.snapshot.id, recordedAt: at, actor, note: payload.note?.trim(), desiredSignature: rosterSignature(correction.current) }
  return { ...state, rosterActions: [...(state.rosterActions ?? []), action] }
}
export function rosterCorrectionsCsv(state: Workspace): string {
  const quote = (value: string) => `"${value.replace(/"/g, '""')}"`
  const format = (rows: RosterSourceRow[]) => rows.map(row => `${row.start}/${row.end} (${row.kind})`).join('; ')
  return '\uFEFF' + [['Manual external roster corrections — not a Quinyx import file'], ['Employee ID', 'Luuta person ID', 'Person', 'Workday', 'Source intervals (UTC)', 'Luuta intervals (UTC)', 'Action', 'Absence reason', 'Reported at (UTC)', 'Verification'], ...rosterCorrections(state).map(item => {
    const absence = [...(state.absences ?? [])].reverse().find(absence => absence.personId === item.personId && item.current.some(row => row.kind === 'unavailable' && absence.start < row.end && absence.end > row.start))
    return [state.people.find(person => person.id === item.personId)?.externalIds.quinyx ?? '', item.personId, state.people.find(person => person.id === item.personId)?.name ?? item.personId, item.businessDate, format(item.source), format(item.current), item.kind, absence?.reason ?? '', absence?.reportedAt ?? '', item.status]
  })].map(row => row.map(quote).join(',')).join('\r\n')
}
